import { useState } from 'react';
import { Link } from 'react-router-dom';

import axios from 'axios';
import { 
  DollarSign, TrendingUp, Clock, Zap, CheckCircle2, 
  ArrowRight, Users, Repeat, Gift, Star
} from 'lucide-react';
import { API_URL } from '../config';

export default function AffiliateSimpleLanding() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    source: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Capture UTM parameters from URL
  const getUtmParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || ''
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    const utmParams = getUtmParams();
    
    try {
      await axios.post(`${API_URL}/api/affiliate/quick-apply`, {
        ...formData,
        landing_page: 'simple-ab',
        source_url: window.location.href,
        ...utmParams
      });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">You're In!</h2>
          <p className="text-slate-600 mb-6">
            Check your email for next steps. We'll have you earning within 24 hours.
          </p>
          <Link 
            to="/"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Website
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Hero Section - Above the fold */}
        <div className="max-w-6xl mx-auto px-4 pt-12 pb-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-bold text-white">UPost</span>
            </Link>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                Limited spots available
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Earn <span className="text-green-400">$75+</span> Every Month<br />
                <span className="text-blue-400">Per Referral</span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Know someone who needs a website? Introduce them to us and earn 
                <strong className="text-white"> recurring income for life</strong>.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">25%</p>
                  <p className="text-sm text-slate-400">Commission</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">90</p>
                  <p className="text-sm text-slate-400">Day Cookie</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">$0</p>
                  <p className="text-sm text-slate-400">To Join</p>
                </div>
              </div>

              {/* Social Proof */}
              <div className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 border-2 border-slate-900 flex items-center justify-center text-white text-xs font-bold">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 text-sm">
                  <span className="text-white font-semibold">127+ partners</span> earning this month
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Start Earning Today</h2>
                <p className="text-slate-600">Get approved in under 24 hours</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    How will you share? (optional)
                  </label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select one...</option>
                    <option value="social">Social Media</option>
                    <option value="blog">Blog/Website</option>
                    <option value="youtube">YouTube</option>
                    <option value="email">Email List</option>
                    <option value="word-of-mouth">Word of Mouth</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {error && (
                  <p className="text-red-600 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      Get My Partner Link
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-500 text-center">
                  By signing up, you agree to our{' '}
                  <Link to="/affiliate/terms" className="text-blue-600 hover:underline">
                    Partner Terms
                  </Link>
                </p>
              </form>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-center gap-4 text-slate-400 text-sm">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Free to join
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Instant approval
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works - Simple */}
        <div className="bg-slate-800/50 py-16 mt-12">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              How It Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">1. Get Your Link</h3>
                <p className="text-slate-400">Sign up and get your unique referral link instantly</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Gift className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">2. Share It</h3>
                <p className="text-slate-400">Tell friends, post on social, or email your contacts</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Repeat className="h-8 w-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">3. Get Paid Monthly</h3>
                <p className="text-slate-400">Earn $75+ every month for as long as they're a client</p>
              </div>
            </div>
          </div>
        </div>

        {/* Earnings Calculator */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              Your Earning Potential
            </h2>
            <p className="text-slate-400 text-center mb-12">
              Based on $299/month average client value at 25% commission
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { referrals: 5, monthly: '$375' },
                { referrals: 10, monthly: '$750' },
                { referrals: 25, monthly: '$1,875' },
                { referrals: 50, monthly: '$3,750' },
              ].map((tier) => (
                <div key={tier.referrals} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all">
                  <p className="text-slate-400 text-sm mb-1">{tier.referrals} referrals</p>
                  <p className="text-3xl font-bold text-green-400">{tier.monthly}</p>
                  <p className="text-slate-500 text-xs">per month</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonial */}
        <div className="pb-16">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center">
              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-xl text-white mb-6 italic">
                "I shared my link on one Facebook post and now earn $900/month passively. 
                Best side hustle I've ever found."
              </p>
              <p className="text-slate-400">
                <span className="text-white font-semibold">David C.</span> — Auto Industry Blogger
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Earning?
            </h2>
            <p className="text-blue-100 mb-8">
              Join 127+ partners already earning recurring income
            </p>
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 bg-white text-blue-600 font-bold py-4 px-8 rounded-lg hover:bg-blue-50 transition-all"
            >
              Get Started Now — It's Free
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-900 py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-slate-500 text-sm">
              © 2026 UPost. All rights reserved.<br />
              <Link to="/privacy" className="hover:text-slate-400">Privacy Policy</Link>
              {' · '}
              <Link to="/affiliate/terms" className="hover:text-slate-400">Partner Terms</Link>
              {' · '}
              <Link to="/affiliate" className="hover:text-slate-400">Full Partner Page</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
